import React, { useMemo, useState } from 'react';
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';
import useAPI from '../../hooks/useAPI';
import useDeleteJenisBeban from '../../hooks/useDeleteJenis_beban';
import usePostJenisBeban from '../../hooks/usePostJenis_beban';
import usePutJenisBeban from '../../hooks/useUpdateJenis_beban';
import endpointsAdmin from '../../Services/endpointAdmin';
import { extractArrayPayload } from './adminScope';

const initialForm = {
  id_jenis_beban: '',
  nama_beban: '',
};

const requiredFieldProps = (message) => ({
  required: true,
  title: message,
  onInvalid: (event) => event.target.setCustomValidity(message),
  onInput: (event) => event.target.setCustomValidity(''),
});

function JenisBebanAdmin() {
  const { data: jenisBebanData, loading, error, refetch } = useAPI(endpointsAdmin.jenis_beban.getAll);
  const { postJenisBeban, loading: creating, error: createError } = usePostJenisBeban();
  const { putJenisBeban, loading: updating, error: updateError } = usePutJenisBeban();
  const { deleteJenisBeban, loading: deleting, error: deleteError } = useDeleteJenisBeban();

  const [form, setForm] = useState(initialForm);
  const [actionMessage, setActionMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const jenisBebanList = useMemo(
    () => extractArrayPayload(jenisBebanData, ['jenis_beban', 'data', 'result', 'rows']),
    [jenisBebanData]
  );

  const clearForm = () => {
    setForm(initialForm);
    setIsEditing(false);
    setActionMessage('');
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (item) => {
    setForm({
      id_jenis_beban: item.id_jenis_beban || item.id || '',
      nama_beban: item.nama_beban || item.nama || '',
    });
    setIsEditing(true);
    setActionMessage('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.nama_beban.trim()) {
      setActionMessage('Nama jenis beban wajib diisi.');
      return;
    }

    let result;
    if (form.id_jenis_beban) {
      result = await putJenisBeban(form.id_jenis_beban, form.nama_beban);
    } else {
      result = await postJenisBeban(form.nama_beban);
    }

    if (result) {
      await refetch();
      setActionMessage(form.id_jenis_beban ? 'Jenis beban berhasil diperbarui.' : 'Jenis beban berhasil ditambahkan.');
      clearForm();
    } else {
      setActionMessage(createError || updateError || 'Terjadi kesalahan saat menyimpan jenis beban.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus jenis beban ini?')) {
      return;
    }

    const deleted = await deleteJenisBeban(id);
    if (deleted) {
      await refetch();
      setActionMessage('Jenis beban berhasil dihapus.');
      if (form.id_jenis_beban === String(id)) {
        clearForm();
      }
    } else {
      setActionMessage(deleteError || 'Gagal menghapus jenis beban.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12">
        <p className="text-slate-600">Memuat data jenis beban...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12">
        <p className="text-red-600">Terjadi kesalahan: {error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-6 lg:px-16 py-10">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Kelola Jenis Beban</h1>
            <p className="mt-2 text-sm text-slate-600">Tambah, ubah, dan hapus jenis beban untuk transaksi pengeluaran.</p>
          </div>
        </div>

        {actionMessage && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-sm">
            {actionMessage}
          </div>
        )}

        <div className="grid gap-6 xl:grid-cols-[1.4fr_0.9fr]">
          <section className="rounded-[28px] border border-slate-200 bg-white shadow-sm p-6">
            <div className="flex items-center justify-between gap-4 pb-4 border-b border-slate-200">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Daftar Jenis Beban</h2>
                <p className="text-sm text-slate-500">Semua kategori jenis beban dalam sistem.</p>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700">
                {jenisBebanList.length} item
              </div>
            </div>

            <div className="mt-6 overflow-x-auto">
              <table className="min-w-full text-left">
                <thead className="border-b border-slate-200 text-slate-700">
                  <tr>
                    <th className="px-3 py-3 text-sm font-semibold">Nama Jenis Beban</th>
                    <th className="px-3 py-3 text-sm font-semibold">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {jenisBebanList.length === 0 ? (
                    <tr>
                      <td colSpan="2" className="px-3 py-8 text-center text-sm text-slate-500">Belum ada jenis beban.</td>
                    </tr>
                  ) : (
                    jenisBebanList.map((item) => {
                      const id = item.id_jenis_beban || item.id || '';
                      return (
                        <tr key={id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-3 py-3 text-sm text-slate-700">{item.nama_beban || item.nama || '-'}</td>
                          <td className="px-3 py-3 text-sm text-slate-700">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => handleEdit(item)}
                                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                              >
                                <FiEdit2 /> Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(id)}
                                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-100"
                              >
                                <FiTrash2 /> Hapus
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-[28px] border border-slate-200 bg-white shadow-sm p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-slate-900">{isEditing ? 'Ubah Jenis Beban' : 'Tambah Jenis Beban'}</h2>
              <p className="text-sm text-slate-500">Masukkan nama jenis beban untuk digunakan pada pengeluaran.</p>
            </div>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium text-slate-700">Nama Jenis Beban</label>
                <input
                  type="text"
                  name="nama_beban"
                  value={form.nama_beban}
                  onChange={handleChange}
                  {...requiredFieldProps('Nama jenis beban wajib diisi.')}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  placeholder="Contoh: Biaya Operasional"
                />
                <p className="mt-2 text-xs text-slate-500">Wajib diisi sebelum menyimpan jenis beban.</p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={creating || updating}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isEditing ? 'Simpan Perubahan' : 'Buat Jenis Beban'}
                </button>
                <button
                  type="button"
                  onClick={clearForm}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                >
                  Batal
                </button>
              </div>

              {(createError || updateError || deleteError) && (
                <p className="text-sm text-red-600">{createError || updateError || deleteError}</p>
              )}
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}

export default JenisBebanAdmin;
